import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Sparkles, BookOpen, FileText, Music, Play, Users, 
  Star, ArrowRight, Check, Heart, Zap, ChevronLeft, ChevronRight, Quote
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Placeholder women images for community section
const placeholderAvatars = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop&crop=face',
];

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [memberAvatars, setMemberAvatars] = useState([]);

  const testimonials = [
    {
      quote: "Heather's lived experience and wisdom helps us to listen to our bodies and our minds and live aligned with what we need to light ourselves up inside and find contentment. A beautiful and generous guide for living gracefully and honouring ourselves, so that we can love others and put our energy out into the world for good.",
      author: "Jessica B."
    },
    {
      quote: "I felt that I had someone I could trust.",
      author: "Tee B."
    },
    {
      quote: "She explained the importance of moving things from dreaming to accomplishing.",
      author: "Virginia L."
    },
    {
      quote: "My pathway became easy to see and understand.",
      author: "Karen A."
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Fetch member avatars
  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const response = await axios.get(`${API_URL}/members/avatars`);
        setMemberAvatars(response.data);
      } catch (error) {
        console.error('Failed to fetch avatars:', error);
      }
    };
    fetchAvatars();
  }, []);

  // Get display avatars - use member photos if available, fallback to placeholders
  const getDisplayAvatars = () => {
    const avatars = [];
    // Add real member avatars first
    memberAvatars.forEach(member => {
      if (member.avatar_url) {
        const url = (member.avatar_url.startsWith('/api/uploads/') || member.avatar_url.startsWith('/uploads/'))
          ? `${process.env.REACT_APP_BACKEND_URL}${member.avatar_url}`
          : member.avatar_url;
        avatars.push({ url, name: member.first_name });
      }
    });
    // Fill remaining slots with placeholders (up to 4 total)
    const remaining = 4 - avatars.length;
    for (let i = 0; i < remaining && i < placeholderAvatars.length; i++) {
      avatars.push({ url: placeholderAvatars[i], name: '' });
    }
    return avatars.slice(0, 4);
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="blob blob-purple w-[500px] h-[500px] -top-64 -right-64 fixed opacity-30" />
      <div className="blob blob-turquoise w-[400px] h-[400px] top-1/2 -left-48 fixed opacity-25" />
      <div className="blob blob-orange w-[350px] h-[350px] -bottom-32 right-1/4 fixed opacity-25" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-syne font-bold text-lg text-stone-900 leading-tight">Heather Stewart</span>
              <span className="text-xs text-stone-500 -mt-0.5">Transformation Guide</span>
            </div>
          </Link>
          
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')} className="btn-cosmic" data-testid="dashboard-btn">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-stone-700 hover:text-primary transition-colors font-medium" data-testid="login-nav-link">
                  Sign In
                </Link>
                <Button onClick={() => navigate('/register')} className="btn-cosmic" data-testid="get-started-nav-btn">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full font-medium">
                <Heart className="w-4 h-4" />
                The light to help you find your way
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-stone-900 leading-tight">
                Grow with Support, Accountability, and{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-accent">
                  Real Momentum
                </span>
              </h1>
              
              <p className="text-xl text-stone-600 max-w-xl leading-relaxed">
                I'm Heather — and I guide women who are ready for more than inspiration. They're ready for ownership.
                Inside this space, you'll be seen, supported, and encouraged to grow — steadily, honestly, and together.
                You don't have to do this alone.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleGetStarted} 
                  className="btn-cosmic text-lg px-10 py-6"
                  data-testid="hero-get-started-btn"
                >
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="btn-secondary text-lg"
                  data-testid="hero-pricing-btn"
                >
                  View Premium
                </Button>
              </div>
              
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {getDisplayAvatars().map((avatar, i) => (
                    <div 
                      key={i} 
                      className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-primary to-secondary overflow-hidden"
                    >
                      <img 
                        src={avatar.url} 
                        alt={avatar.name || 'Community member'} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-stone-600">
                  Join other women on this journey
                </p>
              </div>
            </div>
            
            <div className="lg:col-span-5 relative animate-fade-in-up animate-delay-200">
              <div className="relative">
                <img 
                  src="https://static.showit.co/1600/iubDHhGdT9uTuPT995IOGw/160323/prosperity-flow-coaching-heather-headshots-november-2020-15.jpg"
                  alt="Heather Stewart - Transformation Guide"
                  className="rounded-3xl shadow-2xl shadow-purple-500/20"
                />
                <div className="absolute -bottom-6 -left-6 card-cosmic p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">Free Content</p>
                      <p className="text-sm text-stone-600">No credit card needed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Content Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-4">
              Illuminate Your{' '}
              <span className="text-primary">Worth</span>
            </h2>
            <p className="text-xl text-stone-600 max-w-2xl mx-auto">
              A grounded, honest guide for women who look capable on the outside but know something deeper is calling them forward.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: BookOpen, title: 'Free Ebook', desc: 'Read the full 12-chapter book', descBold: 'Illuminate Your Worth: Stop Pretending You\'re Okay and Start Living Like You Mean It', descAfter: '— available inside the app.', color: 'purple' },
              { icon: FileText, title: 'Journals', desc: 'Reflection journals to help you slow down, gain clarity, and reconnect with yourself.', descItalic: 'Download free digital copies or order beautifully printed versions.', color: 'turquoise' },
              { icon: Music, title: 'Meditations', desc: 'Guided meditation recordings for grounding, clarity, and coming back to yourself', color: 'orange' },
              { icon: Play, title: 'Gentle Movement', desc: 'Short guided practice recordings — including yoga, tai chi, and qigong — to help you reconnect with your body, restore your energy, and move with ease.', color: 'purple' },
            ].map((item, i) => (
              <div 
                key={i}
                className="card-cosmic p-8 card-hover animate-fade-in-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className={`w-14 h-14 rounded-2xl bg-cosmic-${item.color}/10 flex items-center justify-center mb-6`}>
                  <item.icon className={`w-7 h-7 text-cosmic-${item.color}`} />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-2">{item.title}</h3>
                <p className="text-stone-600">
                  {item.desc}
                  {item.descBold && <> <strong>{item.descBold}</strong></>}
                  {item.descAfter && <> {item.descAfter}</>}
                </p>
                {item.descItalic && <p className="text-stone-500 text-sm italic mt-2">{item.descItalic}</p>}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={handleGetStarted}
              className="btn-cosmic"
              data-testid="access-free-btn"
            >
              Access Free Content
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-stone-900 relative overflow-hidden">
        <div className="blob blob-purple w-[400px] h-[400px] -top-32 -right-32 opacity-20" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                You Don't Have to Do{' '}
                <span className="text-secondary">This Alone</span>
              </h2>
              <p className="text-xl text-stone-300 leading-relaxed">
                This is a steady space for women who are ready to grow — without hustling, hiding, or drifting.
              </p>
              <p className="text-lg text-stone-400">Inside, you'll find:</p>
              <ul className="space-y-4">
                {[
                  'Honest conversations that help you see clearly',
                  'A circle of women committed to ownership',
                  'Support that encourages movement — not perfection',
                  'A place to practice showing up differently'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-stone-300">
                    <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-secondary" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-stone-400 italic">
                No pressure. No performance.<br />
                Just real growth, together.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://static.showit.co/file/KDmaeYSEOGsJoL0R26Bsgg/160323/web_background-5.png"
                alt="Women in community together"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="card-cosmic p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/20 to-accent/20 blur-3xl" />
            
            <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="badge-premium inline-flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Premium Membership
                </div>
                <h2 className="text-4xl font-bold text-stone-900">
                  Stay Anchored
                </h2>
                <p className="text-lg text-stone-600">
                  Premium gives you structure, proximity, and accountability so the shift doesn't fade.
                </p>
                <ul className="space-y-3">
                  {[
                    'Live workshops',
                    'Private women\'s community',
                    'WhatsApp access',
                    '1:1 strategy sessions',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-stone-700">
                      <Check className="w-5 h-5 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-stone-700 font-medium italic">Ownership is sustained here.</p>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="btn-cosmic"
                  data-testid="view-pricing-btn"
                >
                  View Pricing
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div>
                <img 
                  src="https://static.showit.co/file/VAKicQdUFs84pzOHgfZLvw/160323/web_background-6.png"
                  alt="Women in workshop together"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Carousel */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-stone-900 mb-2">Client Love</h2>
            <p className="text-stone-600">What women are saying about working with Heather</p>
          </div>
          
          <div className="card-cosmic p-8 md:p-12 relative">
            <Quote className="w-12 h-12 text-primary/20 absolute top-6 left-6" />
            
            <div className="min-h-[200px] flex flex-col items-center justify-center text-center px-4 md:px-12">
              <blockquote className="text-xl md:text-2xl text-stone-700 italic leading-relaxed mb-6 transition-opacity duration-500">
                "{testimonials[currentTestimonial].quote}"
              </blockquote>
              <p className="text-primary font-semibold">— {testimonials[currentTestimonial].author}</p>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <button 
                onClick={prevTestimonial}
                className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                data-testid="prev-testimonial-btn"
              >
                <ChevronLeft className="w-5 h-5 text-stone-600" />
              </button>
              
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentTestimonial(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      i === currentTestimonial ? 'bg-primary w-6' : 'bg-stone-300 hover:bg-stone-400'
                    }`}
                    data-testid={`testimonial-dot-${i}`}
                  />
                ))}
              </div>
              
              <button 
                onClick={nextTestimonial}
                className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                data-testid="next-testimonial-btn"
              >
                <ChevronRight className="w-5 h-5 text-stone-600" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-stone-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-syne font-bold text-stone-900">Heather Stewart</span>
              <span className="text-xs text-stone-500">Transformation Guide</span>
            </div>
          </div>
          <p className="text-stone-600 text-sm">
            © 2024 Heather Stewart Coaching. The light to help you find your way.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
